import type { ComponentProps, ReactNode, Ref } from "react";
import type { LucideIcon } from "lucide-react";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

type ButtonProps = {
  variant?: ButtonVariant;
  size?: "default" | "sm";
  isLoading?: boolean;
  icon?: LucideIcon;
  ref?: Ref<HTMLButtonElement>;
  children: ReactNode;
} & Omit<ComponentProps<"button">, "children">;

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-brand-green text-white hover:bg-brand-green-hover disabled:hover:bg-brand-green",
  secondary: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:hover:bg-white",
  danger: "bg-danger text-white hover:opacity-90 disabled:hover:opacity-100",
  ghost: "text-brand-navy hover:bg-gray-100 disabled:hover:bg-transparent",
};

/** Buttons disable and show a spinner during async work. */
export function Button({ variant = "primary", size = "default", isLoading = false, icon: Icon, ref, children, className, disabled, type = "button", ...rest }: ButtonProps) {
  const sizing = size === "sm" ? "min-h-[36px] px-3 py-1.5 text-sm" : "min-h-[44px] px-4 py-2 text-sm";

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green-50 disabled:cursor-default disabled:opacity-60 ${sizing} ${VARIANT[variant]} ${className ?? ""}`}
      {...rest}
    >
      {isLoading ? <Spinner size="sm" tone={variant === "secondary" || variant === "ghost" ? "brand" : "inverse"} /> : Icon ? <Icon size={16} aria-hidden="true" /> : null}
      {children}
    </button>
  );
}
